/**
 * Data Collection Questions Configuration
 *
 * Defines the questions for Screen 2 of the V2 screening flow.
 * Questions are organized by priority (P1 = must collect, P2 = nice to have)
 * and support various input types with conditional sub-questions.
 */

import { DataCollectionQuestion } from './types';

// ---------------------------------------------------------------------------
// Motor Vehicle Accident (MVA) Questions
// ---------------------------------------------------------------------------

export const MVA_DATA_COLLECTION_QUESTIONS: DataCollectionQuestion[] = [
  // -------------------------------------------------------------------------
  // P1 Questions (Must Collect)
  // Order is optimized for visual balance in 2-column layout:
  // Left column (even indices): police_report, client_insurance, medical_treatment
  // Right column (odd indices): personal_info, at_fault_insurance
  // -------------------------------------------------------------------------
  {
    id: 'police_report',
    text: 'Was a police report filed?',
    inputType: 'yes_no_unknown',
    priority: 'P1',
    sectionLabel: 'Incident Details',
    formDataPath: 'incident_info.police_report',
    subQuestions: [
      {
        id: 'police_report.department',
        text: 'Police Department',
        inputType: 'text',
        placeholder: 'e.g., Houston PD, Harris County Sheriff...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'incident_info.police_dept',
      },
      {
        id: 'police_report.report_number',
        text: 'Report Number',
        inputType: 'text',
        placeholder: 'Enter report number...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'incident_info.police_report_number',
      },
      {
        id: 'police_report.other_driver_ticket',
        text: 'Did the other driver receive a ticket?',
        inputType: 'yes_no_unknown',
        showCondition: { parentAnswer: 'any' },
        formDataPath: 'incident_info.other_driver_ticket',
      },
      {
        id: 'police_report.other_driver_fault',
        text: 'Did the other driver admit fault at the scene?',
        inputType: 'yes_no_unknown',
        showCondition: { parentAnswer: 'any' },
        formDataPath: 'incident_info.other_driver_fault',
      },
    ],
  },
  {
    id: 'personal_info',
    text: 'Personal Information',
    inputType: 'group',
    priority: 'P1',
    sectionLabel: 'Personal Information',
    subQuestions: [
      {
        id: 'personal_info.dob',
        text: 'Date of Birth',
        inputType: 'date',
        required: true,
        formDataPath: 'personal_info.dob',
      },
      {
        id: 'personal_info.ssn',
        text: 'Social Security Number',
        inputType: 'text',
        placeholder: 'XXX-XX-XXXX',
        required: true,
        formDataPath: 'personal_info.ssn_last_4',
      },
      {
        id: 'personal_info.address',
        text: 'Physical Address',
        inputType: 'text',
        placeholder: 'Enter full address...',
        required: true,
        formDataPath: 'personal_info.address',
      },
      {
        id: 'personal_info.emergency_contact',
        text: 'Emergency Contact',
        inputType: 'text',
        placeholder: 'Name and phone number...',
        formDataPath: 'personal_info.emergency_contact.name',
      },
    ],
  },
  {
    id: 'client_insurance',
    text: 'Were you the driver of the vehicle?',
    inputType: 'yes_no_unknown',
    priority: 'P1',
    sectionLabel: 'Insurance Information',
    subQuestions: [
      // If YES (was driver) - ask about their own auto insurance
      {
        id: 'client_insurance.has_insurance',
        text: 'Do you have auto insurance?',
        inputType: 'yes_no_unknown',
        showCondition: { parentAnswer: 'yes' },
        helperText: 'If details aren\'t available, leave fields blank.',
      },
      // These fields only show if client HAS auto insurance
      {
        id: 'client_insurance.carrier',
        text: 'Carrier',
        inputType: 'text',
        placeholder: 'e.g., State Farm, Geico...',
        showCondition: { questionId: 'client_insurance.has_insurance', answer: 'yes' },
        formDataPath: 'insurance.pip_insurance.company_name',
      },
      {
        id: 'client_insurance.policy_number',
        text: 'Policy Number',
        inputType: 'text',
        placeholder: 'Enter policy number...',
        showCondition: { questionId: 'client_insurance.has_insurance', answer: 'yes' },
        formDataPath: 'insurance.pip_insurance.policy_number',
      },
      {
        id: 'client_insurance.claim_number',
        text: 'Claim Number',
        inputType: 'text',
        placeholder: 'Enter claim number...',
        showCondition: { questionId: 'client_insurance.has_insurance', answer: 'yes' },
        formDataPath: 'insurance.pip_insurance.claim_number',
      },
      // If NO (was passenger) - ask about the vehicle's insurance
      {
        id: 'client_insurance.has_vehicle_insurance',
        text: 'Do you have the insurance information for the vehicle you were in?',
        inputType: 'yes_no_unknown',
        showCondition: { parentAnswer: 'no' },
        helperText: 'If details aren\'t available, leave fields blank.',
      },
      // These fields only show if they HAVE the vehicle's insurance info
      {
        id: 'client_insurance.vehicle_carrier',
        text: 'Carrier',
        inputType: 'text',
        placeholder: 'e.g., State Farm, Geico...',
        showCondition: { questionId: 'client_insurance.has_vehicle_insurance', answer: 'yes' },
        formDataPath: 'insurance.pip_insurance.company_name',
      },
      {
        id: 'client_insurance.vehicle_policy_number',
        text: 'Policy Number',
        inputType: 'text',
        placeholder: 'Enter policy number...',
        showCondition: { questionId: 'client_insurance.has_vehicle_insurance', answer: 'yes' },
        formDataPath: 'insurance.pip_insurance.policy_number',
      },
      {
        id: 'client_insurance.vehicle_claim_number',
        text: 'Claim Number',
        inputType: 'text',
        placeholder: 'Enter claim number...',
        showCondition: { questionId: 'client_insurance.has_vehicle_insurance', answer: 'yes' },
        formDataPath: 'insurance.pip_insurance.claim_number',
      },
    ],
  },
  {
    id: 'at_fault_insurance',
    text: 'Does the at-fault party have auto insurance?',
    inputType: 'yes_no_unknown',
    priority: 'P1',
    helperText: 'If details aren\'t available, leave fields blank.',
    subQuestions: [
      {
        id: 'at_fault_insurance.carrier',
        text: 'Carrier',
        inputType: 'text',
        placeholder: 'e.g., State Farm, Geico...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'insurance.defendants.0.insurance_company_name',
      },
      {
        id: 'at_fault_insurance.policy_number',
        text: 'Policy Number',
        inputType: 'text',
        placeholder: 'Enter policy number...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'insurance.defendants.0.policy_number',
      },
      {
        id: 'at_fault_insurance.claim_number',
        text: 'Claim Number',
        inputType: 'text',
        placeholder: 'Enter claim number...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'insurance.defendants.0.claim_number',
      },
    ],
  },
  {
    id: 'medical_treatment',
    text: 'Medical treatment providers',
    inputType: 'object_array',
    priority: 'P1',
    sectionLabel: 'Medical Treatment',
    formDataPath: 'medical_wage_history.treatments',
    objectArrayConfig: {
      fields: [
        {
          key: 'provider_name',
          label: 'Provider Name',
          type: 'text',
          placeholder: 'e.g., Memorial Hermann, Dr. Smith...',
          required: true,
        },
        {
          key: 'treatment_type',
          label: 'Treatment Type',
          type: 'text',
          placeholder: 'e.g., ER, Chiropractor, PT...',
        },
        {
          key: 'start_date',
          label: 'Start Date',
          type: 'date',
        },
        {
          key: 'end_date',
          label: 'End Date',
          type: 'date',
        },
        {
          key: 'details',
          label: 'Treatment Details',
          type: 'textarea',
          placeholder: 'Notes about treatment, injuries addressed, etc...',
        },
      ],
      addButtonText: 'Add Provider',
      displayFormat: '{provider_name} · {treatment_type}',
    },
  },
  {
    id: 'client_health_insurance',
    text: 'Do you have health insurance?',
    inputType: 'yes_no_unknown',
    priority: 'P1',
    helperText: 'If details aren\'t available, leave fields blank.',
    subQuestions: [
      {
        id: 'client_health_insurance.carrier',
        text: 'Carrier',
        inputType: 'text',
        placeholder: 'e.g., Blue Cross Blue Shield, United Healthcare...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'insurance.health_provider',
      },
      {
        id: 'client_health_insurance.policy_number',
        text: 'Member ID',
        inputType: 'text',
        placeholder: 'Enter member ID...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'insurance.health_member_id',
      },
      {
        id: 'client_health_insurance.claim_number',
        text: 'Group ID',
        inputType: 'text',
        placeholder: 'Enter group ID...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'insurance.health_group_id',
      },
    ],
  },

  // -------------------------------------------------------------------------
  // P2 Questions (Nice to Have)
  // -------------------------------------------------------------------------
  {
    id: 'photos',
    text: 'Do you have photos of the accident, vehicles, or injuries?',
    inputType: 'yes_no_unknown',
    priority: 'P2',
    sectionLabel: 'Additional Information',
    formDataPath: 'incident_info.photos',
    subQuestions: [
      {
        id: 'photos.request_send',
        text: 'Can you send them to us?',
        inputType: 'ask_client',
        askClientPrompt: 'Ask the client to email or text photos of the accident scene, vehicle damage, and any visible injuries.',
        showCondition: { parentAnswer: 'yes' },
      },
    ],
  },
  {
    id: 'witnesses',
    text: 'Were there any witnesses to the accident?',
    inputType: 'yes_no_unknown',
    priority: 'P2',
    formDataPath: 'incident_info.witnesses',
    subQuestions: [
      {
        id: 'witnesses.contact_info',
        text: 'Witness contact information',
        inputType: 'text',
        placeholder: 'Names and phone numbers...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'incident_info.witness_contact_info',
      },
    ],
  },
  {
    id: 'prior_injuries',
    text: 'Did you have any prior injuries to the same body parts?',
    inputType: 'yes_no_unknown',
    priority: 'P2',
    formDataPath: 'medical_wage_history.prior_injuries',
    subQuestions: [
      {
        id: 'prior_injuries.description',
        text: 'Please describe the prior injuries',
        inputType: 'text',
        placeholder: 'Describe previous injuries...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'medical_wage_history.prior_injuries_description',
      },
    ],
  },
  {
    id: 'vehicle_type',
    text: 'What type of vehicle were you in?',
    inputType: 'text',
    priority: 'P2',
    placeholder: 'Car, truck, motorcycle, bicycle, pedestrian...',
    formDataPath: 'incident_info.vehicle_type',
  },
];

// ---------------------------------------------------------------------------
// Premises Liability (PL) Questions
// ---------------------------------------------------------------------------

export const PL_DATA_COLLECTION_QUESTIONS: DataCollectionQuestion[] = [
  // -------------------------------------------------------------------------
  // P1 Questions (Must Collect)
  // All IDs prefixed with 'pl_' to prevent cross-contamination with MVA
  // Explicit column assignment for visual balance:
  // Left column (5): incident_location, owner_knew, witnesses, health_insurance, medical_treatment
  // Right column (3): personal_info, property_owner, photos_video
  // -------------------------------------------------------------------------
  {
    id: 'pl_incident_location',
    text: 'Where exactly on the property did the incident occur?',
    inputType: 'text',
    priority: 'P1',
    sectionLabel: 'Incident Details',
    placeholder: 'e.g., parking lot, entrance, bathroom, aisle 5, stairwell...',
    column: 'left',
    formDataPath: 'incident_info.location',
  },
  {
    id: 'pl_personal_info',
    text: 'Personal Information',
    inputType: 'group',
    priority: 'P1',
    sectionLabel: 'Personal Information',
    column: 'right',
    subQuestions: [
      {
        id: 'pl_personal_info.dob',
        text: 'Date of Birth',
        inputType: 'date',
        required: true,
        formDataPath: 'personal_info.dob',
      },
      {
        id: 'pl_personal_info.ssn',
        text: 'Social Security Number',
        inputType: 'text',
        placeholder: 'XXX-XX-XXXX',
        required: true,
        formDataPath: 'personal_info.ssn_last_4',
      },
      {
        id: 'pl_personal_info.address',
        text: 'Physical Address',
        inputType: 'text',
        placeholder: 'Enter full address...',
        required: true,
        formDataPath: 'personal_info.address',
      },
      {
        id: 'pl_personal_info.emergency_contact',
        text: 'Emergency Contact',
        inputType: 'text',
        placeholder: 'Name and phone number...',
        formDataPath: 'personal_info.emergency_contact.name',
      },
    ],
  },
  {
    id: 'pl_owner_knew',
    text: 'Do you believe the property owner knew about the hazard before your incident?',
    inputType: 'yes_no_unknown',
    priority: 'P1',
    column: 'left',
    formDataPath: 'incident_info.owner_knew_about_hazard',
    subQuestions: [
      {
        id: 'pl_owner_knew.how_know',
        text: 'How do you know?',
        inputType: 'text',
        placeholder: 'Explain how they would have known...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'incident_info.owner_knew_details',
      },
    ],
  },
  {
    id: 'pl_property_owner',
    text: 'Do you know who owns or manages the property?',
    inputType: 'yes_no_unknown',
    priority: 'P1',
    column: 'right',
    subQuestions: [
      {
        id: 'pl_property_owner.name',
        text: 'Name of owner/manager/company',
        inputType: 'text',
        placeholder: 'e.g., Walmart, ABC Property Management...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'insurance.defendants.0.name',
      },
    ],
  },
  {
    id: 'pl_witnesses',
    text: 'Were there any witnesses?',
    inputType: 'yes_no_unknown',
    priority: 'P1',
    sectionLabel: 'Evidence',
    column: 'left',
    formDataPath: 'incident_info.witnesses',
    subQuestions: [
      {
        id: 'pl_witnesses.contact_info',
        text: 'Witness contact information',
        inputType: 'text',
        placeholder: 'Names and phone numbers...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'incident_info.witness_contact_info',
      },
    ],
  },
  {
    id: 'pl_photos_video',
    text: 'Did you take photos or video of the hazard or your injuries?',
    inputType: 'yes_no_unknown',
    priority: 'P1',
    column: 'right',
    formDataPath: 'incident_info.photos',
    subQuestions: [
      {
        id: 'pl_photos_video.security_cameras',
        text: 'Are there security cameras in the area?',
        inputType: 'yes_no_unknown',
        showCondition: { parentAnswer: 'any' },
        formDataPath: 'incident_info.security_cameras',
      },
    ],
  },
  {
    id: 'pl_health_insurance',
    text: 'Do you have health insurance?',
    inputType: 'yes_no_unknown',
    priority: 'P1',
    sectionLabel: 'Insurance Information',
    column: 'left',
    helperText: 'If details aren\'t available, leave fields blank.',
    subQuestions: [
      {
        id: 'pl_health_insurance.carrier',
        text: 'Carrier',
        inputType: 'text',
        placeholder: 'e.g., Blue Cross Blue Shield, United Healthcare...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'insurance.health_provider',
      },
      {
        id: 'pl_health_insurance.policy_number',
        text: 'Member ID',
        inputType: 'text',
        placeholder: 'Enter member ID...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'insurance.health_member_id',
      },
      {
        id: 'pl_health_insurance.claim_number',
        text: 'Group ID',
        inputType: 'text',
        placeholder: 'Enter group ID...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'insurance.health_group_id',
      },
    ],
  },
  {
    id: 'pl_medical_treatment',
    text: 'Medical treatment providers',
    inputType: 'object_array',
    priority: 'P1',
    sectionLabel: 'Medical Treatment',
    column: 'left',
    formDataPath: 'medical_wage_history.treatments',
    objectArrayConfig: {
      fields: [
        {
          key: 'provider_name',
          label: 'Provider Name',
          type: 'text',
          placeholder: 'e.g., Memorial Hermann, Dr. Smith...',
          required: true,
        },
        {
          key: 'treatment_type',
          label: 'Treatment Type',
          type: 'text',
          placeholder: 'e.g., ER, Chiropractor, PT...',
        },
        {
          key: 'start_date',
          label: 'Start Date',
          type: 'date',
        },
        {
          key: 'end_date',
          label: 'End Date',
          type: 'date',
        },
        {
          key: 'details',
          label: 'Treatment Details',
          type: 'textarea',
          placeholder: 'Notes about treatment, injuries addressed, etc...',
        },
      ],
      addButtonText: 'Add Provider',
      displayFormat: '{provider_name} · {treatment_type}',
    },
  },

  // -------------------------------------------------------------------------
  // P2 Questions (Nice to Have)
  // -------------------------------------------------------------------------
  {
    id: 'pl_prior_injuries',
    text: 'Did you have any prior injuries to the same body parts?',
    inputType: 'yes_no_unknown',
    priority: 'P2',
    sectionLabel: 'Additional Information',
    formDataPath: 'medical_wage_history.prior_injuries',
    subQuestions: [
      {
        id: 'pl_prior_injuries.description',
        text: 'Please describe the prior injuries',
        inputType: 'text',
        placeholder: 'Describe previous injuries...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'medical_wage_history.prior_injuries_description',
      },
    ],
  },
  {
    id: 'pl_similar_incidents',
    text: 'Have you heard of similar incidents happening at this location?',
    inputType: 'yes_no_unknown',
    priority: 'P2',
    formDataPath: 'incident_info.similar_incidents',
    subQuestions: [
      {
        id: 'pl_similar_incidents.filed_complaint',
        text: 'Did you file a complaint or incident report with the property?',
        inputType: 'yes_no_unknown',
        showCondition: { parentAnswer: 'any' },
        formDataPath: 'incident_info.filed_complaint',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Mold Questions
// ---------------------------------------------------------------------------

export const MOLD_DATA_COLLECTION_QUESTIONS: DataCollectionQuestion[] = [
  // -------------------------------------------------------------------------
  // P1 Questions (Must Collect) - All 11 questions are P1 for mold cases
  // All IDs prefixed with 'mold_' to prevent cross-contamination
  // Mold questions use incident_info for mold-specific fields (no sync mappings existed before)
  // -------------------------------------------------------------------------
  {
    id: 'mold_injuries',
    text: 'What injuries or ailments do you have from mold?',
    inputType: 'text',
    priority: 'P1',
    sectionLabel: 'Injuries & Medical',
    placeholder: 'e.g., respiratory illness, brain fog, rashes, GI issues...',
    column: 'left',
    formDataPath: 'incident_info.present_complaints',
  },
  {
    id: 'mold_doctors',
    text: 'What doctors have you seen for mold? What is your diagnosis?',
    inputType: 'yes_no_unknown',
    priority: 'P1',
    column: 'right',
    subQuestions: [
      {
        id: 'mold_doctors.details',
        text: 'Doctor/facility name and diagnosis',
        inputType: 'text',
        placeholder: 'e.g., Dr. Smith — diagnosed with respiratory illness...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'incident_info.mold_doctors_details',
      },
      {
        id: 'mold_doctors.location',
        text: 'Where did you receive treatment?',
        inputType: 'text',
        placeholder: 'City, state...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'incident_info.mold_doctors_location',
      },
    ],
  },
  {
    id: 'mold_cause',
    text: 'What caused the mold and when?',
    inputType: 'group',
    priority: 'P1',
    sectionLabel: 'Mold Details',
    column: 'left',
    subQuestions: [
      {
        id: 'mold_cause.date',
        text: 'When did you first notice the mold?',
        inputType: 'date',
        required: true,
        formDataPath: 'incident_info.date',
      },
      {
        id: 'mold_cause.description',
        text: 'Description of the cause',
        inputType: 'text',
        placeholder: 'e.g., leaking pipes, poorly sealed windows, HVAC system, exterior landscaping...',
        required: true,
        formDataPath: 'incident_info.description',
      },
    ],
  },
  {
    id: 'mold_landlord_knowledge',
    text: 'Did your landlord know about the mold and fail to fix it?',
    inputType: 'yes_no_unknown',
    priority: 'P1',
    column: 'right',
    formDataPath: 'incident_info.mold_landlord_knowledge',
    subQuestions: [
      {
        id: 'mold_landlord_knowledge.details',
        text: 'How did the landlord know? What did they do or fail to do?',
        inputType: 'text',
        placeholder: 'e.g., told them multiple times about a leak, sent handyman but problem persisted...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'incident_info.mold_landlord_knowledge_details',
      },
    ],
  },
  {
    id: 'mold_test',
    text: 'Did you have a mold test completed with air sample testing?',
    inputType: 'yes_no_unknown',
    priority: 'P1',
    sectionLabel: 'Evidence',
    column: 'left',
    formDataPath: 'incident_info.mold_test',
    subQuestions: [
      {
        id: 'mold_test.results',
        text: 'What were the results?',
        inputType: 'text',
        placeholder: 'Describe the test results...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'incident_info.mold_test_results',
      },
    ],
  },
  {
    id: 'mold_photos',
    text: 'Do you have photographs of the mold and injuries?',
    inputType: 'yes_no_unknown',
    priority: 'P1',
    column: 'right',
    formDataPath: 'incident_info.photos',
    subQuestions: [
      {
        id: 'mold_photos.request_send',
        text: 'Can you send them to us?',
        inputType: 'ask_client',
        askClientPrompt: 'Ask the client to email or text photos of the mold, water damage, and any visible injuries or skin conditions.',
        showCondition: { parentAnswer: 'yes' },
      },
    ],
  },
  {
    id: 'mold_communications',
    text: 'Do you have emails, texts, or letters to landlord/property manager about the mold?',
    inputType: 'yes_no_unknown',
    priority: 'P1',
    column: 'left',
    formDataPath: 'incident_info.mold_communications',
    subQuestions: [
      {
        id: 'mold_communications.request_send',
        text: 'Can you send copies to us?',
        inputType: 'ask_client',
        askClientPrompt: 'Ask the client to forward any written communications (emails, texts, letters) about the mold issue.',
        showCondition: { parentAnswer: 'yes' },
      },
    ],
  },
  {
    id: 'mold_landlord',
    text: 'Who is your landlord and property manager?',
    inputType: 'group',
    priority: 'P1',
    sectionLabel: 'Property Information',
    column: 'right',
    subQuestions: [
      {
        id: 'mold_landlord.name',
        text: 'Landlord Name',
        inputType: 'text',
        placeholder: 'Name of landlord or management company...',
        required: true,
        formDataPath: 'insurance.defendants.0.name',
      },
      {
        id: 'mold_landlord.property_manager',
        text: 'Property Manager',
        inputType: 'text',
        placeholder: 'Name of property manager (if different)...',
        formDataPath: 'incident_info.mold_property_manager',
      },
    ],
  },
  {
    id: 'mold_hoa',
    text: 'Do you have an HOA?',
    inputType: 'yes_no_unknown',
    priority: 'P1',
    column: 'left',
    formDataPath: 'incident_info.mold_hoa',
  },
  {
    id: 'mold_property_damage',
    text: 'Do you have property damage? How much is your property loss?',
    inputType: 'yes_no_unknown',
    priority: 'P1',
    sectionLabel: 'Damages',
    column: 'right',
    formDataPath: 'incident_info.mold_property_damage',
    subQuestions: [
      {
        id: 'mold_property_damage.amount',
        text: 'Estimated property loss (actual cost value)',
        inputType: 'text',
        placeholder: 'e.g., $5,000 — furniture, clothing, electronics...',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'incident_info.mold_property_damage_amount',
      },
    ],
  },
  {
    id: 'mold_residence_status',
    text: 'Are you still living at the subject residence?',
    inputType: 'yes_no_unknown',
    priority: 'P1',
    column: 'left',
    formDataPath: 'incident_info.mold_residence_status',
    subQuestions: [
      {
        id: 'mold_residence_status.moved_details',
        text: 'When did you move out? Were you moved out during tenancy?',
        inputType: 'text',
        placeholder: 'Describe move-out date or temporary relocations...',
        showCondition: { parentAnswer: 'no' },
        formDataPath: 'incident_info.mold_moved_details',
      },
      {
        id: 'mold_residence_status.plans_to_move',
        text: 'Do you plan to move out?',
        inputType: 'yes_no_unknown',
        showCondition: { parentAnswer: 'yes' },
        formDataPath: 'incident_info.mold_plans_to_move',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Helper to get questions by case type
// ---------------------------------------------------------------------------

export function getDataCollectionQuestions(caseType: string): DataCollectionQuestion[] {
  const normalizedType = caseType?.toLowerCase().replace(/\s+/g, '_') ?? '';

  // Motor Vehicle Accident
  if (normalizedType === 'motor_vehicle' || normalizedType.includes('motor') || normalizedType === 'auto_accident') {
    return MVA_DATA_COLLECTION_QUESTIONS;
  }

  // Premises Liability
  if (normalizedType === 'premise_liability' || normalizedType === 'premises_liability') {
    return PL_DATA_COLLECTION_QUESTIONS;
  }

  // Mold
  if (normalizedType === 'mold') {
    return MOLD_DATA_COLLECTION_QUESTIONS;
  }

  // Other case types have no P1/P2 questions defined
  return [];
}

// ---------------------------------------------------------------------------
// Helper to check if a question is complete
// ---------------------------------------------------------------------------

export function isQuestionComplete(
  question: DataCollectionQuestion,
  answers: Record<string, { value: string | undefined; asked: boolean }>
): boolean {
  const mainAnswer = answers[question.id];

  // For group type, check sub-questions
  if (question.inputType === 'group') {
    if (!question.subQuestions?.length) return true;

    // Check if at least the required sub-questions are answered
    return question.subQuestions.every((sub) => {
      if (!sub.required) return true;
      const subAnswer = answers[sub.id];
      return subAnswer?.value !== undefined && subAnswer.value !== '';
    });
  }

  // For ask_client type, check if asked
  if (question.inputType === 'ask_client') {
    return mainAnswer?.asked === true;
  }

  // For other types, check if value exists
  if (!mainAnswer?.value) return false;

  // Check sub-questions if any
  if (question.subQuestions?.length) {
    return question.subQuestions.every((sub) => {
      // Check show condition to determine if sub-question is visible
      if (sub.showCondition) {
        // Check if it's a parentAnswer condition (legacy format)
        if ('parentAnswer' in sub.showCondition) {
          if (sub.showCondition.parentAnswer !== 'any' &&
              mainAnswer.value !== sub.showCondition.parentAnswer) {
            return true; // Not shown, so considered complete
          }
        } else {
          // It's a questionId condition (nested format)
          const targetAnswer = answers[sub.showCondition.questionId];
          if (targetAnswer?.value !== sub.showCondition.answer) {
            return true; // Not shown, so considered complete
          }
        }
      }

      const subAnswer = answers[sub.id];

      // For ask_client sub-questions, check asked status
      if (sub.inputType === 'ask_client') {
        return subAnswer?.asked === true;
      }

      // For required fields, must have value
      if (sub.required) {
        return subAnswer?.value !== undefined && subAnswer.value !== '';
      }

      // Optional fields are always "complete"
      return true;
    });
  }

  return true;
}
